import { buildIndex, search } from './faq/search_engine'

const QUESTION_WEIGHT = 3
const KEYWORD_WEIGHT = 2
const ANSWER_WEIGHT = 1
const DEBOUNCE_MS = 120

// textContent runs list items together ("statementsGeneral"), so join text nodes with spaces.
function plainText(element) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  const parts = []
  while (walker.nextNode()) parts.push(walker.currentNode.nodeValue)
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}

function collapseFor(entry) {
  return entry.querySelector('.accordion-collapse')
}

function setOpen(entry, open) {
  window.bootstrap.Collapse.getOrCreateInstance(collapseFor(entry), {
    toggle: false,
  })[open ? 'show' : 'hide']()
}

// Deep links such as /faq#faq-security open that answer, on both the FAQ page and the landing page.
function openFromHash() {
  const id = window.location.hash.slice(1)
  const target =
    id && document.querySelector(`#${CSS.escape(id)}[data-faq-entry]`)
  if (!target) return
  target.hidden = false
  setOpen(target, true)
  target.scrollIntoView({ block: 'start' })
}

function initialiseFaqSearch() {
  const root = document.querySelector('[data-faq-search]')
  if (!root || root.dataset.initialised === 'true') return openFromHash()
  root.dataset.initialised = 'true'

  const input = root.querySelector('[data-faq-search-input]')
  const clear = root.querySelector('[data-faq-search-clear]')
  const status = root.querySelector('[data-faq-search-status]')
  const accordion = document.querySelector('[data-faq-accordion]')
  const empty = document.querySelector('[data-faq-empty]')
  const chips = [...document.querySelectorAll('[data-faq-category-filter]')]
  const entries = [...accordion.querySelectorAll('[data-faq-entry]')]
  const byId = new Map(entries.map((entry) => [entry.dataset.faqId, entry]))

  const index = buildIndex(
    entries.map((entry) => ({
      id: entry.dataset.faqId,
      fields: [
        {
          text: plainText(entry.querySelector('[data-faq-question]')),
          weight: QUESTION_WEIGHT,
        },
        { text: entry.dataset.faqKeywords || '', weight: KEYWORD_WEIGHT },
        {
          text: plainText(entry.querySelector('[data-faq-answer]')),
          weight: ANSWER_WEIGHT,
        },
      ],
    })),
  )

  const showEntries = (visible) => {
    const shown = new Set(visible)
    entries.forEach((entry) => {
      entry.hidden = !shown.has(entry)
    })
    visible.forEach((entry) => accordion.append(entry))
    entries
      .filter((entry) => !shown.has(entry))
      .forEach((entry) => accordion.append(entry))
  }

  const setCategory = (category) => {
    chips.forEach((chip) =>
      chip.setAttribute(
        'aria-pressed',
        String(chip.dataset.faqCategoryFilter === category),
      ),
    )
    showEntries(
      entries.filter(
        (entry) => category === 'all' || entry.dataset.faqCategory === category,
      ),
    )
  }

  const syncUrl = (query) => {
    const url = new URL(window.location.href)
    if (query) url.searchParams.set('q', query)
    else url.searchParams.delete('q')
    window.history.replaceState(window.history.state, '', url)
  }

  const reset = () => {
    clear.hidden = true
    status.textContent = ''
    if (empty) empty.hidden = true
    setCategory('all')
  }

  const run = () => {
    const query = input.value.trim()
    syncUrl(query)
    if (!query) return reset()

    clear.hidden = false
    chips.forEach((chip) =>
      chip.setAttribute(
        'aria-pressed',
        String(chip.dataset.faqCategoryFilter === 'all'),
      ),
    )
    const results = search(query, index).map(({ id }) => byId.get(id))
    showEntries(results)

    if (!results.length) {
      status.textContent = ''
      if (empty) {
        empty.querySelector('[data-faq-empty-query]').textContent = query
        empty.hidden = false
      }
      return
    }

    if (empty) empty.hidden = true
    status.textContent =
      results.length === 1
        ? '1 matching answer'
        : `${results.length} matching answers, best match first`
    entries.forEach((entry) => {
      if (entry !== results[0]) setOpen(entry, false)
    })
    setOpen(results[0], true)
  }

  let timer
  input.addEventListener('input', () => {
    clearTimeout(timer)
    timer = setTimeout(run, DEBOUNCE_MS)
  })
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && input.value) {
      event.preventDefault()
      input.value = ''
      run()
    }
  })
  clear.addEventListener('click', () => {
    input.value = ''
    run()
    input.focus()
  })
  document
    .querySelector('[data-faq-search-reset]')
    ?.addEventListener('click', () => {
      input.value = ''
      run()
      input.focus()
    })
  chips.forEach((chip) =>
    chip.addEventListener('click', () => {
      if (input.value) {
        input.value = ''
        syncUrl('')
        clear.hidden = true
        status.textContent = ''
        if (empty) empty.hidden = true
      }
      setCategory(chip.dataset.faqCategoryFilter)
    }),
  )

  // Searches started from the landing page arrive as /faq?q=...
  const initialQuery = new URLSearchParams(window.location.search).get('q')
  if (initialQuery) {
    input.value = initialQuery
    run()
  } else {
    openFromHash()
  }
}

document.addEventListener('turbo:load', initialiseFaqSearch)
document.addEventListener('DOMContentLoaded', initialiseFaqSearch)
